import { StyleSheet, ViewProps } from 'react-native'
import {
  CalendarValue,
  Spacing,
  TableSort,
  displayTitlesForCalendarValue,
  calendarValueToInsightsFromTo,
  Weekday,
  titleForWeekday,
} from 'zens-storybook'
import { useBuilding, useCompany, useFloor, useInsightsFloor, useStore, useWorkspaceTypes } from 'zens-store'
import { saveSvg } from '@dms/helper'
import { useTranslation } from 'react-i18next'
import * as XLSX from 'xlsx'

import {
  forwardRef,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  ReactElement,
  RefAttributes,
  useImperativeHandle,
} from 'react'

import InsightsHeatmapDetails from './heatmapdetails'

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginTop: Spacing.M,
  },
})

export interface InsightsHeatmapRef {
  download: (key: string) => Promise<void>
}

interface Props extends ViewProps {
  companyId: number
  buildingId: number
  floorId: number
  period: CalendarValue
  weekdays?: Weekday[]
  workingHours?: boolean
  selected?: string
  onSelect?: (id?: string) => void
  sort?: TableSort
  onSortChange?: (sort: TableSort) => void
}

const InsightsHeatmap = forwardRef<InsightsHeatmapRef, Props>((props, ref) => {
  const {
    companyId,
    buildingId,
    floorId,
    period,
    weekdays,
    workingHours,
    selected,
    onSelect,
    sort,
    onSortChange,
    style,
    ...otherProps
  } = props
  const { i18n, message } = useStore()
  const { t } = useTranslation()

  const dates = calendarValueToInsightsFromTo(period, weekdays)
  const insights = useInsightsFloor(
    {
      companyId,
      floorId,
      from: dates?.from || new Date(),
      to: dates?.to || new Date(),
      weekdays,
      workingHours,
    },
    { enabled: !!dates },
  )
  const company = useCompany({ companyId })
  const building = useBuilding({ companyId, buildingId })
  const floor = useFloor({ companyId, floorId })
  const types = useWorkspaceTypes()

  useImperativeHandle(ref, () => ({
    download: (key) => download(key as 'data' | 'heatmap'),
  }))

  async function download(key: 'data' | 'heatmap') {
    if (key === 'data') {
      try {
        const periodTitles = displayTitlesForCalendarValue(period, i18n.language)
        const rows: (string | number)[][] = []
        rows.push([t('Company'), company.data?.name || '-'])
        rows.push([t('Building'), building.data?.name || '-'])
        rows.push([t('Floor'), floor.data?.name || '-'])
        rows.push([t('From'), periodTitles[0] || '-'])
        rows.push([t('To'), periodTitles[1] || '-'])
        rows.push([t('Weekdays'), ...(weekdays?.map((weekday) => titleForWeekday(weekday, t)) || [t('All days')])])
        rows.push([t('Working hours'), workingHours ? t('Yes') : t('No')])
        rows.push([])
        rows.push([t('Workspace name'), t('Workspace type'), t('Occupancy')])

        insights.data
          ?.sort((a, b) =>
            a.name?.localeCompare(b.name, undefined, {
              numeric: true,
              sensitivity: 'base',
            }),
          )
          .forEach((item) =>
            rows.push([
              item.name,
              types.data?.find((type) => type.id === item.workspaceTypeId)?.name || '',
              item.value === null ? '' : item.value,
            ]),
          )
        const worksheet = XLSX.utils.aoa_to_sheet(rows)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Insights')
        XLSX.writeFile(workbook, `${t('Historical heatmap')}.xlsx`, { compression: true })
      } catch (error) {
        message.add({ message: t('Error generating data'), status: 'error' })
        console.error('Error generating data', error)
      }
    } else {
      onSelect?.(undefined)
      setTimeout(() => {
        try {
          saveSvg(`${t('Historical heatmap')}.svg`, 'FloorplanView', 'image/svg+xml')
        } catch (error) {
          message.add({ message: t('Error generating heatmap'), status: 'error' })
          console.error('Error generating heatmap', error)
        }
      }, 0)
    }
  }

  return (
    <InsightsHeatmapDetails
      companyId={companyId}
      buildingId={buildingId}
      floorId={floorId}
      insights={insights.data}
      selected={selected}
      onSelect={onSelect}
      sort={sort}
      onSortChange={onSortChange}
      style={[styles.container, style]}
      {...otherProps}
    />
  )
}) as ForwardRefExoticComponent<PropsWithoutRef<Props> & RefAttributes<InsightsHeatmapRef>> & {
  Skeleton: () => ReactElement
}

InsightsHeatmap.Skeleton = () => {
  return <InsightsHeatmapDetails.Skeleton style={styles.container} />
}

export default InsightsHeatmap
